import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["children", "elementRef", "maxWidth", "variant", "margin", "screenReaderLabel", "onRequestTabChange", "tabOverflow", "styles"];
var _dec, _class, _Tabs;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { cloneElement, Children, Component } from 'react';
import keycode from 'keycode';
import { View } from '@instructure/ui-view';
import { matchComponentTypes, safeCloneElement, passthroughProps } from '@instructure/ui-react-utils';
import { logError as error } from '@instructure/console';
import { uid } from '@instructure/uid';
import { Focusable } from '@instructure/ui-focusable';
import { getBoundingClientRect } from '@instructure/ui-dom-utils';
import { debounce } from '@instructure/debounce';
import { px } from '@instructure/ui-utils';
import { withStyle } from '@instructure/emotion';
import generateStyle from "./styles.js";
import generateComponentTheme from "./theme.js";
import { Tab } from "./Tab/index.js";
import { Panel } from "./Panel/index.js";
import { allowedProps } from "./props.js";
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
---
category: components
---
**/
let Tabs = (_dec = withStyle(generateStyle, generateComponentTheme), _dec(_class = (_Tabs = class Tabs extends Component {
  constructor(props) {
    super(props);
    this._tabList = null;
    this._focusable = null;
    this._tabListPosition = void 0;
    this._debounced = void 0;
    this._resizeListener = void 0;
    this.ref = null;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.handleTabClick = (event, {
      index
    }) => {
      const nextTab = this.getNextTab(index, 0);
      this.fireOnChange(event, nextTab);
    };
    this.handleTabKeyDown = (event, {
      index
    }) => {
      let nextTab;
      if (event.keyCode === keycode.codes.up || event.keyCode === keycode.codes.left) {
        // Select next tab to the left
        nextTab = this.getNextTab(index, -1);
      } else if (event.keyCode === keycode.codes.down || event.keyCode === keycode.codes.right) {
        // Select next tab to the right
        nextTab = this.getNextTab(index, 1);
      }
      if (nextTab) {
        event.preventDefault();
        this.fireOnChange(event, nextTab);
      }
    };
    this.handleResize = () => {
      this.setState({
        withTabListOverflow: this._tabList.scrollWidth > this._tabList.offsetWidth
      });
      this._tabListPosition = getBoundingClientRect(this._tabList);
    };
    this.handleFocusableRef = el => {
      this._focusable = el;
    };
    this.handleTabListRef = el => {
      this._tabList = el;
    };
    this.handleScroll = event => {
      if (this.props.tabOverflow !== 'scroll' || !this.state.withTabListOverflow) {
        event.preventDefault();
        return;
      }
      const tabList = event.currentTarget;
      const scrollLeftMax = Math.round(tabList.scrollWidth - getBoundingClientRect(tabList).width);
      const scrollLeft = Math.floor(Math.abs(tabList.scrollLeft));
      this.setState({
        showStartOverLay: scrollLeft > 0,
        showEndOverLay: scrollLeft < scrollLeftMax
      });
    };
    this.state = {
      withTabListOverflow: false,
      showStartOverLay: false,
      showEndOverLay: false
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    if (this.props.tabOverflow === 'scroll' && this._tabList) {
      this.startScrollOverflow();
    }
    if (this.props.shouldFocusOnRender) {
      this.focus();
    }
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
  }
  componentDidUpdate(prevProps, prevState) {
    var _this$props$makeStyle2, _this$props2;
    if (this.props.shouldFocusOnRender && !prevProps.shouldFocusOnRender) {
      this.focus();
    }

    // start event listeners for scroll overflow
    if (prevProps.tabOverflow === 'stack' && this.props.tabOverflow === 'scroll') {
      this.startScrollOverflow();
    }

    // cancel event listeners for scroll overflow
    if (prevProps.tabOverflow === 'scroll' && this.props.tabOverflow === 'stack') {
      this.cancelScrollOverflow();
    }

    // we need to recalculate the scroll overflow if the style changes
    if (this.props.tabOverflow === 'scroll' && prevProps.styles !== this.props.styles) {
      this.handleResize();
    }

    // when tabList is set as overflown,
    // make sure active tab is always visible
    if (this.props.tabOverflow === 'scroll' && this._tabList && !prevState.withTabListOverflow && this.state.withTabListOverflow) {
      const activeTabEl = this._tabList.querySelector('[aria-selected="true"]');
      this.showActiveTabIfOverlayed(activeTabEl);
    }
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
  }
  componentWillUnmount() {
    this.cancelScrollOverflow();
  }
  startScrollOverflow() {
    this.handleResize();
    this._debounced = debounce(this.handleResize, 300, {
      leading: true,
      trailing: true
    });
    this._tabListPosition = getBoundingClientRect(this._tabList);
    this._resizeListener = new ResizeObserver(entries => {
      for (const entry of entries) {
        const newWidth = entry.contentRect.width;
        if (this._tabListPosition.width !== newWidth) {
          var _this$_debounced;
          (_this$_debounced = this._debounced) === null || _this$_debounced === void 0 ? void 0 : _this$_debounced.call(this);
        }
      }
    });
    this._resizeListener.observe(this._tabList);
  }
  cancelScrollOverflow() {
    if (this._resizeListener) {
      this._resizeListener.disconnect();
    }
    if (this._debounced) {
      this._debounced.cancel();
    }
  }
  getOverlayWidth() {
    const _this$props3 = this.props,
      variant = _this$props3.variant,
      tabOverflow = _this$props3.tabOverflow,
      styles = _this$props3.styles;
    if (styles && tabOverflow === 'scroll') {
      if (variant === 'default') {
        return px(styles === null || styles === void 0 ? void 0 : styles.scrollOverlayWidthDefault);
      } else {
        return px(styles === null || styles === void 0 ? void 0 : styles.scrollOverlayWidthSecondary);
      }
    }
    return 0;
  }
  showActiveTabIfOverlayed(activeTabEl) {
    if (this._tabList && this._tabListPosition && typeof this._tabList.scrollTo === 'function' // test for scrollTo support
    ) {
      const tabPosition = getBoundingClientRect(activeTabEl);
      const tabListPosition = this._tabListPosition;
      const tabListBoundStart = tabListPosition.left + this.getOverlayWidth();
      const tabListBoundEnd = tabListPosition.right + this.getOverlayWidth();
      const tabPositionStart = tabPosition.left;
      const tabPositionEnd = tabPosition.right;
      if (tabListBoundEnd > tabPositionEnd) {
        const offset = Math.round(tabListBoundEnd - tabPositionEnd);
        this._tabList.scrollTo({
          top: 0,
          left: this._tabList.scrollLeft + offset,
          behavior: 'smooth'
        });
      } else if (tabListBoundStart > tabPositionStart) {
        const offset = Math.round(tabListBoundStart - tabPositionStart);
        this._tabList.scrollTo({
          top: 0,
          left: this._tabList.scrollLeft - offset,
          behavior: 'smooth'
        });
      }
    }
  }
  getNextTab(startIndex, step) {
    const tabs = Children.toArray(this.props.children).map(child => matchComponentTypes(child, [Panel]) && child);
    const count = tabs.length;
    const change = step < 0 ? step + count : step;
    error(startIndex >= 0 && startIndex < count, `[Tabs] Invalid tab index: '${startIndex}'.`);
    let nextIndex = startIndex;
    let nextTab;
    do {
      nextIndex = (nextIndex + change) % count;
      nextTab = tabs[nextIndex];
    } while (nextTab && nextTab.props && nextTab.props.isDisabled);
    error(nextIndex >= 0 && nextIndex < count, `[Tabs] Invalid tab index: '${nextIndex}'.`);
    return {
      index: nextIndex,
      id: nextTab.props.id
    };
  }
  fireOnChange(event, {
    index,
    id
  }) {
    if (typeof this.props.onRequestTabChange === 'function') {
      this.props.onRequestTabChange(event, {
        index,
        id
      });
    }

    // this is needed because keypress cancels scrolling. So we have to trigger the scrolling
    // one "tick" later than the keypress
    setTimeout(() => {
      if (this.state.withTabListOverflow) {
        const tab = id ? this._tabList.querySelector(`#tab-${CSS.escape(id)}`) : null;
        this.showActiveTabIfOverlayed(tab);
      }
    }, 0);
  }
  createTab(index, generatedId, selected, panel) {
    const id = panel.props.id || generatedId;
    return _jsx(Tab, {
      variant: this.props.variant,
      id: `tab-${id}`,
      controls: panel.props.id || `panel-${id}`,
      index: index,
      isSelected: selected,
      isDisabled: panel.props.isDisabled,
      onClick: this.handleTabClick,
      onKeyDown: this.handleTabKeyDown,
      isOverflowScroll: this.props.tabOverflow === 'scroll',
      children: panel.props.renderTitle
    }, `tab-${index}`);
  }
  clonePanel(index, generatedId, selected, panel, activePanel) {
    const id = panel.props.id || generatedId;

    // fixHeight can be 0, so simply `fixheight` could return falsy value
    const hasFixedHeight = typeof this.props.fixHeight !== 'undefined';
    const commonProps = {
      id: panel.props.id || `panel-${id}`,
      labelledBy: `tab-${id}`,
      isSelected: selected,
      variant: this.props.variant,
      maxHeight: !hasFixedHeight ? this.props.maxHeight : void 0,
      minHeight: !hasFixedHeight ? this.props.minHeight : '100%'
    };
    let activePanelClone = null;
    if (activePanel !== void 0) {
      // cloning active panel with a proper custom key as a workaround because
      // safeCloneElement overwrites it with the key from the original element
      activePanelClone = /*#__PURE__*/cloneElement(activePanel, {
        key: `panel-${index}`
      });
      return safeCloneElement(activePanelClone, {
        padding: activePanelClone.props.padding || this.props.padding,
        textAlign: activePanelClone.props.textAlign || this.props.textAlign,
        ...commonProps
      });
    } else {
      return safeCloneElement(panel, {
        key: `panel-${index}`,
        padding: panel.props.padding || this.props.padding,
        textAlign: panel.props.textAlign || this.props.textAlign,
        ...commonProps
      });
    }
  }
  focus() {
    this._focusable && typeof this._focusable.focus === 'function' && this._focusable.focus();
  }
  render() {
    const panels = [];
    const tabs = [];
    const _this$props4 = this.props,
      children = _this$props4.children,
      elementRef = _this$props4.elementRef,
      maxWidth = _this$props4.maxWidth,
      variant = _this$props4.variant,
      margin = _this$props4.margin,
      screenReaderLabel = _this$props4.screenReaderLabel,
      onRequestTabChange = _this$props4.onRequestTabChange,
      tabOverflow = _this$props4.tabOverflow,
      styles = _this$props4.styles,
      props = _objectWithoutProperties(_this$props4, _excluded);
    const activePanels = Children.toArray(children).filter(child => matchComponentTypes(child, [Panel])).filter(child => child.props.active);
    if (activePanels.length > 1) {
      error(false, `[Tabs] Only one Panel can be marked as active.`);
    }
    const selectedChildIndex = Children.toArray(children).filter(child => matchComponentTypes(child, [Panel])).findIndex(child => child.props.isSelected && !child.props.isDisabled);
    const selectedIndex = selectedChildIndex >= 0 ? selectedChildIndex : 0;
    Children.toArray(children).map((child, index) => {
      if (matchComponentTypes(child, [Panel])) {
        const selected = !child.props.isDisabled && (child.props.isSelected || selectedIndex === index);
        const id = uid();
        tabs.push(this.createTab(index, id, selected, child));
        if (activePanels.length === 1) {
          panels.push(this.clonePanel(index, id, selected, child, activePanels[0]));
        } else {
          panels.push(this.clonePanel(index, id, selected, child));
        }
      } else {
        panels.push(child);
      }
    });
    const withScrollFade = tabOverflow === 'scroll' && this.state.withTabListOverflow;

    // suppress overlay whenever final Tab is active, or Firefox will cover it
    const startScrollOverlay = this.state.showStartOverLay ? _jsx("span", {
      css: styles === null || styles === void 0 ? void 0 : styles.startScrollOverlay
    }, "start-overlay") : null;
    const endScrollOverlay = this.state.showEndOverLay ? _jsx("span", {
      css: styles === null || styles === void 0 ? void 0 : styles.endScrollOverlay
    }, "end-overlay") : null;
    return _jsxs(View, {
      ...passthroughProps(props),
      elementRef: this.handleRef,
      maxWidth: maxWidth,
      margin: margin,
      as: "div",
      css: styles === null || styles === void 0 ? void 0 : styles.container,
      "data-cid": "Tabs",
      children: [_jsx(Focusable, {
        ref: this.handleFocusableRef,
        children: () => _jsx(View, {
          as: "div",
          position: "relative",
          borderRadius: "medium",
          shouldAnimateFocus: false,
          css: styles === null || styles === void 0 ? void 0 : styles.tabs,
          children: _jsxs(View, {
            as: "div",
            role: "tablist",
            css: styles === null || styles === void 0 ? void 0 : styles.tabList,
            "aria-label": screenReaderLabel,
            elementRef: this.handleTabListRef,
            onScroll: this.handleScroll,
            children: [tabs, withScrollFade && startScrollOverlay, withScrollFade && endScrollOverlay]
          })
        })
      }), _jsx("div", {
        css: styles === null || styles === void 0 ? void 0 : styles.panelsContainer,
        children: panels
      })]
    });
  }
}, _Tabs.displayName = "Tabs", _Tabs.componentId = 'Tabs', _Tabs.allowedProps = allowedProps, _Tabs.defaultProps = {
  variant: 'default',
  shouldFocusOnRender: false,
  tabOverflow: 'stack'
}, _Tabs.Panel = Panel, _Tabs.Tab = Tab, _Tabs)) || _class);
export default Tabs;
export { Tabs, Panel };